"use client";

import { useState } from "react";
import { v7 as uuid } from "uuid";

import { uploadPrescription } from "@/api/saleor/services";
import { formatDate, isAllowedFormat, isHeic } from "@/apiUtils/commonUtils";
import { lensConfigurationSteps } from "@/apiUtils/constants";
import { endPageTransition, startPageTransition } from "@/apiUtils/pageLoadingUtils";
import AttentionIcon from "@/assets/icons/attention.svg?react";
import UploadIcon from "@/assets/icons/upload.svg?react";
import { ConfiguratorStepProps, TranslationData } from "@/common/lib/types";
import Navigation from "@/components/lcp/components/Navigation";
import { useConfiguratorContext } from "@/components/lcp/context/Provider";
import Button from "@/components/ui/button/Button";
import Divider from "@/components/ui/divider/Divider";
import TextField from "@/components/ui/textField/TextField";
import { cn } from "@/lib/tailwind";
import UploadedItem from "./UploadedItem";

export type FileUpload = {
  id: string;
  file: File;
  preview: string;
};

interface IProps extends ConfiguratorStepProps {
  toggle?: () => void;
  customTitle?: string;
  isLaterUpload?: boolean;
}

export const MAX_FILE_SIZE = 5000000;

export const convertHeicToJpg = async (file: File) => {
  let fileNew = file;
  if (typeof window === "undefined") return fileNew;

  const heic2any = require("heic2any");
  try {
    const jpegblob = await heic2any({ blob: file, toType: "image/jpeg", quality: 1 });
    fileNew = new File(
      Array.isArray(jpegblob) ? [...jpegblob] : [jpegblob],
      `${file.name.split(".")?.[0]}.jpg`,
      { type: "image/jpeg" },
    );
  } catch (error) {
    console.error("Error converting heic to jpg", error);
  }

  return fileNew;
};

export const constructPrescriptionError = (name: string, translations: TranslationData) => {
  const nameError = !name.trim()
    ? translations?.lensConfigurator?.lens_config_prescription_name_input_empty_error_message ||
    "Please enter name for prescription"
    : "";
  return nameError;
};

export const constructUploadsError = (
  files: (File | FileUpload)[],
  translations: TranslationData,
) => {
  if (
    files.length > 0 &&
    files.length <= 2 &&
    files.every((file) => file instanceof File && file.size <= MAX_FILE_SIZE)
  ) {
    return "";
  }

  if (files.length === 0) {
    return (
      translations?.lensConfigurator?.lens_config_prescription_media_input_empty_error_message ||
      "Please upload the prescription to continue"
    );
  }

  if (files.length > 2) {
    return (
      translations?.lensConfigurator?.lens_config_prescription_media_input_max_error_message ||
      "Please note: You can add a max of two images. Not all options will be applicable, depending on your given prescription"
    );
  }

  if (!files.every((file) => isAllowedFormat(file instanceof File ? file.type : ""))) {
    return (
      translations?.lensConfigurator
        ?.lens_config_prescription_media_input_unsupported_error_message ||
      "File format not supported"
    );
  }

  let error = "";
  files.forEach((file) => {
    if (file instanceof File && file.size > 5000000)
      error =
        translations?.lensConfigurator?.lens_config_prescription_media_input_size_error_message ||
        "File size limit exceeded";
  });

  return error;
};

const UploadPrescription = ({
  toggle,
  customTitle,
  translations,
  isLaterUpload,
  handleStepData,
  handleJumpToStep,
}: IProps) => {
  const { loading } = useConfiguratorContext();
  const [uploads, setUploads] = useState<FileUpload[]>([]);
  const [prescriptionName, setPrescriptionName] = useState("");

  const [uploading, setUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({ name: "", upload: "" });

  const currentStep = lensConfigurationSteps.UPLOAD_PRESCRIPTION;
  const showBack = typeof toggle === "function" || typeof handleJumpToStep === "function";

  const handleGoBack = async () => {
    if (typeof toggle === "function") {
      toggle();
      return;
    }

    if (typeof handleJumpToStep === "function") {
      await handleJumpToStep(lensConfigurationSteps.PRESCRIPTION);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    const files = Array.from(fileList);

    for (let index = 0; index < files.length; index++) {
      const file = files[index];
      if (!isHeic(file.name)) continue;

      const fileNew = await convertHeicToJpg(file);
      files[index] = fileNew;
    }

    e.target.value = "";

    const error = constructUploadsError(files, translations);
    setValidationErrors((state) => ({ ...state, upload: error }));

    if (error) return;
    setUploads((prevUploads) => {
      const newUploads = files.map((file) => ({
        file,
        id: uuid(),
        preview: URL.createObjectURL(file),
      }));

      // Combine previous uploads with the new files, keeping only the latest two
      return [...prevUploads, ...newUploads].slice(-2);
    });
  };

  const handleRemoveUpload = (id: string) => {
    setUploads((prev) =>
      prev.filter((upload) => {
        if (upload.id !== id) return true;
        URL.revokeObjectURL(upload.preview);
        return false;
      }),
    );
  };

  const handleUploadPrescription = async () => {
    const [image1, image2] = uploads.map((item) => item.file);

    const formData = new FormData();
    if (image1) formData.append("1", image1);
    if (image2) formData.append("2", image2);

    formData.append(
      "map",
      JSON.stringify({
        ...(image1 ? { "1": ["variables.image1"] } : {}),
        ...(image2 ? { "2": ["variables.image2"] } : {}),
      }),
    );

    const response = await uploadPrescription(formData, prescriptionName);
    const instance = response?.instance;

    if (isLaterUpload) {
      await handleStepData(currentStep, instance);
    }

    return handleStepData(currentStep, {
      ...{ ...instance, source: instance?.source, date: formatDate(instance?.createdDate) },
      newPrescription: true,
    });
  };

  const handleValidate = () => {
    const prescriptionError = constructPrescriptionError(prescriptionName, translations);
    const uploadsError = constructUploadsError(
      uploads.map((upload) => upload.file),
      translations,
    );
    setValidationErrors({ name: prescriptionError, upload: uploadsError });
    return prescriptionError === "" && uploadsError === "";
  };

  const handleCleanUp = () => {
    uploads.forEach((upload) => URL.revokeObjectURL(upload.preview));
    setUploads([]);
    setPrescriptionName("");
  };

  const handleContinue = async () => {
    startPageTransition(false);
    setUploading(true);

    if (!handleValidate()) {
      endPageTransition();
      setUploading(false);
      return;
    }

    await handleUploadPrescription();
    endPageTransition();
    setUploading(false);
    handleCleanUp();
  };

  const handleCancel = async () => {
    if (typeof toggle === "function") {
      toggle();
      return;
    }
    startPageTransition(false);
    await handleJumpToStep?.(lensConfigurationSteps.PRESCRIPTION);
    endPageTransition();
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        {showBack && (
          <Navigation onGoBack={handleGoBack} className="hidden md:flex max-sm:px-4">
            {customTitle ??
              translations?.lensConfigurator
                ?.lens_config_upload_prescription_section_back_button_label}
          </Navigation>
        )}
        <h1 className={cn("text-sm font-bold max-sm:px-4", !showBack && "uppercase")}>
          {customTitle ??
            translations?.lensConfigurator?.lens_config_upload_prescription_section_title}
        </h1>
        <p className="text-[0.75rem] text-tertiary-6 max-sm:px-4">
          <span className="mr-1 font-semibold">
            {
              translations?.lensConfigurator
                ?.lens_config_upload_prescription_section_subtitle_bold_text
            }
          </span>
          <span>
            {translations?.lensConfigurator?.lens_config_upload_prescription_section_subtitle}
          </span>
        </p>
      </div>
      <Divider containerClassName="mb-6" />

      {/* Upload Form */}
      <div className="grid grid-cols-2 gap-8">
        <div className="col-span-full lg:col-span-1 max-sm:px-4">
          <TextField
            value={prescriptionName}
            onChange={(e) => setPrescriptionName(e.target.value)}
            placeholder={
              translations?.lensConfigurator?.lens_config_prescription_name_input_placeholder
            }
            className={cn(
              validationErrors.name && "ring-2 ring-secondary-1 focus:ring-secondary-1",
            )}
            slots={{
              error: { error: validationErrors.name, className: "-mx-0.5" },
              label: {
                parent: { className: cn(validationErrors.name && "text-secondary-1") },
                label: translations?.lensConfigurator?.lens_config_prescription_name_input_label,
              },
              ...(validationErrors.name && {
                endAction: { action: <AttentionIcon className="-mr-2" /> },
              }),
            }}
          />
          <div
            className={cn(
              "mt-4 border-2 border-dashed p-8 text-center",
              validationErrors.upload ? "border-secondary-1" : "border-[#C4C4C4]",
            )}
          >
            <div className="mb-4 flex justify-center">
              <UploadIcon />
            </div>
            <input
              multiple
              type="file"
              id="fileInput"
              className="hidden"
              onChange={handleFileSelect}
              accept=".jpg,.jpeg,.png,.pdf"
            />
            <label
              htmlFor="fileInput"
              className="mx-auto mb-2 block w-fit cursor-pointer bg-tertiary-2 px-5 py-1 text-sm font-bold text-white"
            >
              {translations?.lensConfigurator?.lens_config_prescription_upload_input_area_title ||
                "Browse or Upload"}
            </label>
            <p className="text-sm text-tertiary-6">
              {translations?.lensConfigurator
                ?.lens_config_prescription_upload_input_area_description ||
                "Files Supported: .jpg, .pdf, .png"}
            </p>
          </div>
          {validationErrors.upload && (
            <div className="bg-[#FAE3DE] p-4">
              <p className="text-secondary-1">{validationErrors.upload}</p>
            </div>
          )}
        </div>

        {/* Uploads List */}
        {uploads.length > 0 && (
          <div className="col-span-full lg:col-span-1">
            <h2 className="mb-4 text-sm font-medium max-sm:px-4">Uploads</h2>
            <div className="space-y-3 max-sm:px-4">
              {uploads.map((upload) => (
                <UploadedItem
                  key={upload.id}
                  translations={translations}
                  onRemove={() => handleRemoveUpload(upload.id)}
                  file={{
                    name: upload.file.name,
                    previewURL: upload.file.type === "application/pdf" ? "PDF" : upload.preview,
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row max-sm:px-4">
        <Button disabled={uploading || loading} className="w-full" onClick={handleContinue}>
          {translations?.lensConfigurator?.lens_config_prescription_upload_continue_button_label ||
            "Continue"}
        </Button>
        {showBack && (
          <Button
            variant="outlined"
            className="w-full"
            onClick={handleCancel}
            disabled={uploading || loading}
          >
            {translations?.lensConfigurator?.lens_config_prescription_upload_cancel_button_label ||
              "Cancel"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default UploadPrescription;
